import FundDetailsTabRow from 'components/Pages/FundsCenter/FundDetails/FundDetailsTabRow';
import FundDetailsTabSkeleton from 'components/Pages/FundsCenter/FundDetails/FundDetailsTabSkeleton';
import useHelpers from 'hooks/helpers';
import useStripedTableHelper from 'hooks/useStripedTableHelper';
import FundDetailsStripedTable from 'components/Pages/FundsCenter/FundDetails/FirstLevelTabs/FundDetailsStripedTable';
import DisplayingRange from 'components/includes/DisplayingRange';
import Pagination from 'components/includes/Pagination';
import ManualDisclaimer from 'components/Generic/ManualDisclaimer';
import { useEffect, useMemo, useState } from 'react';
import { useI18n } from 'next-localization';
import usePayload from 'hooks/useFundDetailsRequestPayload';
import { standardFormat } from '@/constants/dateFormats';
import {
  DownloadFile,
  useLazyFetchHoldingsTabDataQuery,
  useLazyFetchHoldingsTabManualDisclaimerDataQuery,
} from '@/store/fundDetails/services/fundDetailsApi';
import AlertMessageBase from 'components/Generic/AlertMessageBase';
import AbrdnButton from 'components/Generic/Buttons/AbrdnButton';
import { getAvailableTabs } from '@/store/fundDetails';
import { AvailableTabs } from '@/store/fundDetails/typings';
import { useSelector } from 'react-redux';
import { AxiosResponse } from 'axios';

const FundDetailsHoldingsTab = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [skip, setSkip] = useState(0);
  const { t } = useI18n();
  const { getErrorDictionary, localizeDate } = useHelpers();
  const availableTabs: AvailableTabs | null = useSelector(getAvailableTabs);

  const holdingType = useMemo(() => {
    return Object.keys(availableTabs.holdingsTab).find(
      (key) => key !== 'tab' && availableTabs.holdingsTab[key] === true
    );
  }, [availableTabs]);

  const generalPayload = { ...usePayload() };

  const payload = useMemo(() => {
    return {
      ...generalPayload,
      topHoldings: holdingType === 'topHoldings',
      holdingType:
        holdingType === 'dailyHoldings' ? 0 : holdingType === 'quarterlyHoldings' ? 2 : 1,
      take: 20,
      skip,
    };
  }, [skip, holdingType]);

  const [fetchHoldingsTabData, { isFetching, data, error }] = useLazyFetchHoldingsTabDataQuery();
  const [fetchDisclaimerData, { data: disclaimerData }] =
    useLazyFetchHoldingsTabManualDisclaimerDataQuery();

  const { tableData, isScrollable } = useStripedTableHelper(data, isFetching);

  const dictionary = useMemo(() => {
    const dictionaryItems = {
      title: t('fundDetailsTabsDailyHoldings'),
      downloadBtn: t('downloadDailyHoldingsBtn'),
    };

    switch (holdingType) {
      case 'monthlyHoldings':
        dictionaryItems.title = t('fundDetailsTabsMonthlyHoldings');
        dictionaryItems.downloadBtn = t('downloadMonthlyHoldingsBtn');
        break;
      case 'topHoldings':
        dictionaryItems.title = t('fundDetailsTabsTopTenHoldings');
        break;
      case 'quarterlyHoldings':
        dictionaryItems.title = t('fundDetailsTabsQuarterlyHoldings');
        dictionaryItems.downloadBtn = t('downloadQuarterlyHoldingsBtn');
        break;
      default:
        return dictionaryItems;
    }

    return dictionaryItems;
  }, [holdingType, t]);

  const translationsForBackEnd = useMemo(() => {
    return {
      fundDetailsTabsDailyHoldings: t('fundDetailsTabsDailyHoldings'),
      fundDetailsTabsMonthlyHoldings: t('fundDetailsTabsMonthlyHoldings'),
      fundDetailsTabsQuarterlyHoldings: t('fundDetailsTabsQuarterlyHoldings'),
      fundDetailsHoldingsReportDate: t('fundDetailsHoldingsReportDate'),
      fundDetailsTabsHoldings: t('fundDetailsTabsHoldings'),
    };
  }, [t]);

  const reportDate = useMemo(() => {
    return (
      t('fundDetailsHoldingsReportDate') + ' ' + localizeDate(data?.reportDate, standardFormat)
    );
  }, [data, localizeDate, t]);

  const downloadDocument = async () => {
    const { fund, holdingType, language, site } = payload;
    try {
      const data: AxiosResponse<Blob> = await DownloadFile({
        fund,
        holdingType,
        language,
        site,
        translationLabels: translationsForBackEnd,
      });
      createAndDownloadFile(data);
    } catch (error) {
      console.error(error);
    }
  };

  const onPageChange = (currentPage) => {
    setCurrentPage(+currentPage);
    setSkip(20 * +currentPage - 20);
  };

  const createAndDownloadFile = (data) => {
    if (typeof window !== 'undefined') {
      const a = document.createElement('a');
      a.href = window.URL.createObjectURL(data.data);
      a.download = `${dictionary.title}`;
      document.body.appendChild(a);

      a.click();
      document.body.removeChild(a);
    }
  };

  useEffect(() => {
    fetchHoldingsTabData(payload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, payload]);

  useEffect(() => {
    fetchHoldingsTabData(payload);
    fetchDisclaimerData(payload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const element = document.getElementById('holdingsTable');
    if (element) {
      element.scrollIntoView();
    }
  }, [isFetching]);

  return (
    <section className="default-section-padding background-white">
      <FundDetailsTabRow fullWidth>
        <h2>{t('fundDetailsTabsHoldings')}</h2>

        <div className="mt-5 mt-lg-8">
          <h3>{dictionary.title}</h3>

          <div className="mt-5">
            {isFetching ? (
              <FundDetailsTabSkeleton />
            ) : error ? (
              <AlertMessageBase type="error" title={t(getErrorDictionary(error))} />
            ) : data && data?.length !== 0 ? (
              <>
                <FundDetailsStripedTable
                  reportDate={reportDate}
                  isScrollable={isScrollable}
                  data={tableData}
                  tableId="holdingsTable"
                  wrapperId="holdingsWrapper"
                />

                {holdingType !== 'topHoldings' && (
                  <div className="d-flex flex-column flex-md-row mt-4 align-items-start align-items-md-center justify-content-between">
                    <DisplayingRange
                      class="footnote text-grey mb-5 mb-md-0"
                      dictionaryKey="paginationDisplayingResults"
                      total={data?.totalResults}
                      take="20"
                      page={currentPage}
                    />
                    {data?.totalResults && (
                      <Pagination
                        currentPage={currentPage}
                        maxPages={Math.ceil(data?.totalResults / 20)}
                        className="pagination justify-content-center text-grey mb-5 mb-md-0"
                        onSelect={onPageChange}
                        isFetching={isFetching}
                      />
                    )}
                    <AbrdnButton id="download" buttonType="primary" onClick={downloadDocument}>
                      <span>{dictionary.downloadBtn}</span>
                      <i className="bi bi-download ps-1" aria-hidden="true" />
                    </AbrdnButton>
                  </div>
                )}

                {disclaimerData && disclaimerData.length !== 0 && (
                  <ManualDisclaimer disclaimerData={disclaimerData} className="mt-8" />
                )}
              </>
            ) : (
              <div className="body-small">{t('noResultsMessage')}</div>
            )}
          </div>
        </div>
      </FundDetailsTabRow>
    </section>
  );
};

export default FundDetailsHoldingsTab;
